import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect
} from 'react';
import styles from './OrganizationList.module.scss';
import { H2 } from '@heading';
import { ArrowLink } from '@input';
import classNames from 'classnames';

export default function OrganizationList({
  items,
  className,
  children,
  ...restProps
}) {
  const [hasLogos, setHasLogos] = useState(() => {
    return !items?.length ? false : !!items.find(i => !!i.logo);
  });

  const setHasLogo = useCallback(hasLogo => {
    if (hasLogo) {
      setHasLogos(true);
    }
  }, []);

  const [minHeight, _setMinHeight] = useState(null);

  // Account for changed logo heights when window resizes
  const resizeTimer = useRef(null);
  const resizedMinHeight = useRef(null);

  const setMinHeight = useCallback((minHeight, forceRecalculate) => {
    _setMinHeight(prevMinHeight => {
      clearTimeout(resizeTimer.current);

      if (forceRecalculate) {
        if (
          resizedMinHeight.current === null ||
          minHeight > resizedMinHeight.current
        ) {
          resizedMinHeight.current = minHeight;
        }

        resizeTimer.current = setTimeout(() => {
          _setMinHeight(resizedMinHeight.current);
          resizedMinHeight.current = null;
          resizeTimer.current = null;
        }, 40);

        return prevMinHeight;
      } else {
        return Math.ceil(Math.max(prevMinHeight, minHeight));
      }
    });
  }, []);

  const wrapper = useRef(null);
  const forceRecalculateTimer = useRef(null);

  const forceCalculateMinHeight = useCallback(() => {
    clearTimeout(forceRecalculateTimer.current);

    forceRecalculateTimer.current = setTimeout(() => {
      const items = wrapper?.current.querySelectorAll(styles.logo);

      if (items?.length) {
        let minHeight = 0;

        items.forEach(item => {
          minHeight = Math.ceil(Math.max(minHeight, item.offsetHeight));
        });

        if (minHeight) {
          _setMinHeight(minHeight);
        }
      }
    }, 40);
  }, []);

  return !items?.length && !children ? null : (
    <ul
      ref={wrapper}
      className={classNames(styles.list, className, {
        [styles.noLogos]: !hasLogos
      })}
      {...restProps}
    >
      {items?.map((o, index) => (
        <Organization
          key={index}
          setHasLogo={setHasLogo}
          minHeight={minHeight}
          setMinHeight={setMinHeight}
          forceCalculateMinHeight={forceCalculateMinHeight}
          {...o}
        />
      ))}
      {children}
    </ul>
  );
}

const Organization = React.memo(
  ({
    logo,
    name,
    text,
    url,
    className,
    linkText = 'Learn More',
    logoClassName,
    logoWidth,
    logoWidthSmall,
    logoOffsetTop,
    logoOffsetLeft,
    setHasLogo,
    minHeight,
    setMinHeight,
    forceCalculateMinHeight
  }) => {
    const logoRef = useRef(null);
    const prevMinHeight = useRef(null);
    const updateMinHeight = useCallback(
      isResizing => {
        if (logoRef.current?.complete) {
          // const minHeight = logoRef.current.getBoundingClientRect().height;
          const minHeight = logoRef.current.offsetHeight;
          const offset = logoOffsetTop ?? 0;
          const newMinHeight = Math.ceil(minHeight + offset);

          if (
            prevMinHeight.current === null ||
            prevMinHeight.current !== newMinHeight
          ) {
            prevMinHeight.current = newMinHeight;
            setMinHeight(newMinHeight, isResizing === true);
          }
        }
      },
      [logoOffsetTop, setMinHeight]
    );

    useLayoutEffect(updateMinHeight, [
      logoWidth,
      logoWidthSmall,
      logoOffsetTop
    ]);

    useEffect(() => {
      setHasLogo(!!logo);
    }, [logo, setHasLogo]);

    const getCurrentLogoWidth = useCallback(() => {
      return typeof window !== 'undefined' &&
        logoWidthSmall &&
        window.innerWidth <= 680
        ? logoWidthSmall
        : logoWidth;
    }, [logoWidth, logoWidthSmall]);

    const [currentLogoWidth, setCurrentLogoWidth] = useState(
      getCurrentLogoWidth
    );
    const prevLogoWidth = useRef(currentLogoWidth);

    useEffect(() => {
      const handler = () => {
        setCurrentLogoWidth(getCurrentLogoWidth());
        updateMinHeight(true);
      };

      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    }, [updateMinHeight, getCurrentLogoWidth]);

    useEffect(() => {
      if (currentLogoWidth !== prevLogoWidth.current) {
        prevLogoWidth.current = currentLogoWidth;
        forceCalculateMinHeight();
      }
    }, [currentLogoWidth, forceCalculateMinHeight]);

    let logoComponent = !logo ? null : (
      <img
        src={logo}
        alt={name}
        ref={logoRef}
        style={{
          width: currentLogoWidth
        }}
        className={classNames(styles.logo, logoClassName)}
        onLoad={updateMinHeight}
      />
    );

    if (url) {
      logoComponent = <a href={url}>{logoComponent}</a>;
    }

    const textComponent = text
      ?.trim()
      .split(/\n+/)
      .map((t, index) => {
        t = t?.trim();
        return !t ? null : <p key={index}>{t}</p>;
      })
      .filter(t => t);

    return (
      <li
        className={className}
        style={{ minHeight }}
      >
        {logoComponent && (
          <div
            className={styles.logoWrapper}
            style={{ top: logoOffsetTop, left: logoOffsetLeft }}
          >
            {logoComponent}
          </div>
        )}
        <div className={styles.content}>
          {(name || textComponent) && (
            <div className={styles.text}>
              {name && <H2 className="h3">{name}</H2>}
              {textComponent}
            </div>
          )}
          {url && linkText && (
            <ArrowLink
              href={url}
              className={styles.link}
            >
              {linkText}
            </ArrowLink>
          )}
        </div>
      </li>
    );
  }
);

export { Organization };
